<template>
  <div class="fill-height">
    <line-chart
      v-if="viewMode === 'chart'"
      class="fill-height"
      :raw-data="players"
      :chart-value-keys="chartValueKeys"
      x-value="last_name"
      :y-value-prefix="aggregation && !removable ? 'latest_stats.' : 'player_stats.'"
      :yaxis="[{min: 0}]"
      :xaxis="{
        labels: {
          rotate: -45
        },
        categories: players.map(p => p.first_name[0] + '. ' + p.last_name ),
        tickPlacement: 'between'
      }"
      :colors="zoneColors"
      :markers="{ colors: zoneColors }"
      :fill="{ opacity: zoneColors.map(() => 0.5) }"
    >
      <template v-slot:header>
        <v-card-title class="pa-4">
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <div
                class="subtitle-2 grey--text text--grey-100"
                style="word-break: normal"
              >
                {{ aggregation ? $t(`variables.${aggregation}.long`) + ":" : '' }} {{ $t("card_title.hr_zone_analysis") }}
              </div>
            </v-col>
            <v-col
              cols="auto"
              class="ml-3"
            >
              <v-btn
                v-if="removable"
                icon
                style="pointer-events: auto"
                @click="$emit('remove')"
              >
                <v-icon small>
                  fal fa-times
                </v-icon>
              </v-btn>
              <visual-list-toggle
                v-else
                :value.sync="viewMode"
              />
            </v-col>
          </v-row>
        </v-card-title>
      </template>
    </line-chart>

    <v-card
      v-else-if="viewMode === 'table'"
      class="fill-height rounded-lg"
    >
      <v-card-title class="pa-4">
        <v-row
          no-gutters
          align="center"
        >
          <v-col>
            <div class="subtitle-2 grey--text text--grey-100">
              {{ aggregation ? $t(`variables.${aggregation}.long`) + ":" : '' }} {{ $t("card_title.hr_zone_analysis") }}
            </div>
          </v-col>
          <v-col
            cols="auto"
            class="ml-3"
          >
            <visual-list-toggle
              v-if="!removable"
              :value.sync="viewMode"
            />
          </v-col>
        </v-row>
      </v-card-title>

      <chart-list-view-table
        :headers="tableHeaders"
        :items="players"
      >
        <template v-slot:item.last_name="{ item }">
          {{ item.last_name }}, {{ item.first_name }}
        </template>
      </chart-list-view-table>
    </v-card>
  </div>
</template>

<script>
import LineChart from '@/components/charts/LineChart.vue'
import VisualListToggle from '@/components/buttons/VisualListToggle.vue'
import ChartListViewTable from '@/components/charts/implementation/ChartListViewTable.vue'
import { hasSixZoneSetup } from '@/utils/helpers'

const DEFAULT_LABELS = [
  'hr_variables.regeneration',
  'hr_variables.extensive_aerobic_zone',
  'hr_variables.intensive_aerobic_zone',
  'hr_variables.anaerobic_threshold_zone',
  'hr_variables.vo2_max_zone'
]

const DEFAULT_COLORS = ['#b3b3b3', '#008FFB', '#00E396', '#FEB019', '#FF4560']
const SIX_ZONE_COLORS = ['#b3b3b3', '#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0']

export default {
  name: 'HrZoneChart',
  components: { ChartListViewTable, LineChart, VisualListToggle },
  props: {
    players: {
      type: Array,
      required: true
    },
    removable: {
      type: Boolean,
      default: false
    },
    aggregation: {
      type: String,
      default: null
    }
  },
  data: () => ({
    viewMode: 'chart'
  }),
  computed: {
    isSixZone() {
      return hasSixZoneSetup()
    },
    zoneCount() {
      return this.isSixZone ? 6 : 5
    },
    zoneColors() {
      return this.isSixZone ? SIX_ZONE_COLORS : DEFAULT_COLORS
    },
    zoneEntries() {
      const addAggregation = (key) => this.aggregation && !this.removable ? `${key}_${this.aggregation}` : key
      const entries = []
      for (let i = 1; i <= this.zoneCount; i++) {
        entries.push({
          key: addAggregation(`ZONE${i}DR`),
          rawKey: `ZONE${i}DR`,
          label: this.isSixZone
            ? this.$t('variables.hr_zone_n', { n: i })
            : this.$t(DEFAULT_LABELS[i - 1])
        })
      }
      return entries
    },
    chartValueKeys() {
      return this.zoneEntries.map(e => ({ key: e.key, type: 'bar', label: e.label }))
    },
    tableHeaders() {
      const valuePrefix = this.aggregation && !this.removable ? 'latest_stats.' : 'player_stats.'
      return [
        {
          text: this.$t('table_headers.name'),
          value: 'last_name',
          sortable: true,
          width: '25%'
        },
        ...this.zoneEntries.map(e => ({
          text: e.label,
          value: valuePrefix + e.key,
          sortable: true,
          align: 'center'
        }))
      ]
    }
  }
}
</script>
